import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  UserGroup,
  GroupRole,
  CreateUserGroupRequest,
  UpdateUserGroupRequest,
} from '@/types/userGroups.types';
import { UserGroupService } from '@/services/userGroups.service';

/**
 * User Groups Store State Interface
 */
interface UserGroupsState {
  // Data
  userGroups: UserGroup[];
  groupRoles: { [roleCode: string]: GroupRole };
  groupRolesArray: GroupRole[];

  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;

  // Error states
  error: string | null;

  // Actions
  fetchUserGroupsAndRoles: () => Promise<void>;
  fetchUserGroups: (isMinimal?: boolean) => Promise<void>;
  fetchRoles: () => Promise<void>;
  createUserGroup: (data: CreateUserGroupRequest) => Promise<UserGroup>;
  updateUserGroup: (id: number, data: UpdateUserGroupRequest) => Promise<UserGroup>;
  deleteUserGroup: (id: number) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  userGroups: [],
  groupRoles: {},
  groupRolesArray: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

/**
 * User Groups Store
 */
export const useUserGroupsStore = create<UserGroupsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Fetch both user groups and roles in parallel (mimics Angular forkJoin)
       */
      fetchUserGroupsAndRoles: async () => {
        set({ isLoading: true, error: null });

        try {
          const [userGroups, groupRolesArray] = await Promise.all([
            UserGroupService.getUserGroups(),
            UserGroupService.getRoles(),
          ]);

          // Convert roles array to object with roleCode as key (mimics Angular reduce)
          const groupRoles = groupRolesArray.reduce(
            (acc, role) => {
              acc[role.roleCode] = role;
              return acc;
            },
            {} as { [roleCode: string]: GroupRole }
          );

          set({
            userGroups,
            groupRoles,
            groupRolesArray,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Fetch user groups only
       */
      fetchUserGroups: async (isMinimal = false) => {
        set({ isLoading: true, error: null });

        try {
          const userGroups = await UserGroupService.getUserGroups(isMinimal);
          set({
            userGroups,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Fetch roles only
       */
      fetchRoles: async () => {
        try {
          const groupRolesArray = await UserGroupService.getRoles();
          const groupRoles = groupRolesArray.reduce(
            (acc, role) => {
              acc[role.roleCode] = role;
              return acc;
            },
            {} as { [roleCode: string]: GroupRole }
          );

          set({
            groupRoles,
            groupRolesArray,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({ error: errorMessage });
          throw error;
        }
      },

      /**
       * Create a new user group
       */
      createUserGroup: async (data: CreateUserGroupRequest) => {
        set({ isCreating: true, error: null });

        try {
          const newUserGroup = await UserGroupService.createUserGroup(data);

          // Add the new user group to the current list and sort
          const currentUserGroups = get().userGroups;
          const updatedUserGroups = [...currentUserGroups, newUserGroup].sort(
            (a, b) => a.sort - b.sort
          );

          set({
            userGroups: updatedUserGroups,
            isCreating: false,
            error: null,
          });

          return newUserGroup;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({
            error: errorMessage,
            isCreating: false,
          });
          throw error;
        }
      },

      /**
       * Update an existing user group
       */
      updateUserGroup: async (id: number, data: UpdateUserGroupRequest) => {
        set({ isUpdating: true, error: null });

        try {
          const updatedUserGroup = await UserGroupService.updateUserGroup(id, data);

          // Update the user group in the current list
          const currentUserGroups = get().userGroups;
          const updatedUserGroups = currentUserGroups.map((group) =>
            group.id === id ? { ...group, ...updatedUserGroup } : group
          );

          set({
            userGroups: updatedUserGroups,
            isUpdating: false,
            error: null,
          });

          return updatedUserGroup;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({
            error: errorMessage,
            isUpdating: false,
          });
          throw error;
        }
      },

      /**
       * Delete a user group
       */
      deleteUserGroup: async (id: number) => {
        set({ isDeleting: true, error: null });

        try {
          await UserGroupService.deleteUserGroup(id);

          // Remove the user group from the current list
          const currentUserGroups = get().userGroups;
          const updatedUserGroups = currentUserGroups.filter((group) => group.id !== id);

          set({
            userGroups: updatedUserGroups,
            isDeleting: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({
            error: errorMessage,
            isDeleting: false,
          });
          throw error;
        }
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'user-groups-store',
    }
  )
);
